import React, { useState, useEffect } from 'react';
import './anchor-styles.css';

/**
 * Invoice Detail Modal - Shows full invoice details with approval/rejection
 */
const InvoiceDetailModal = ({ 
  invoiceId, 
  onClose, 
  onApprove, 
  onReject, 
  loading,
  invoice: initialInvoice 
}) => {
  const [invoice, setInvoice] = useState(initialInvoice || null);
  const [showRejectForm, setShowRejectForm] = useState(false);
  const [pdfZoom, setPdfZoom] = useState(100);
  const [pdfRotation, setPdfRotation] = useState(0);
  const [approvalData, setApprovalData] = useState({
    approveFullAmount: true,
    partialAmount: '',
    remarks: '',
    interestRate: 2.5,
    duration: 30
  });
  const [rejectionData, setRejectionData] = useState({
    reason: '',
    comments: ''
  });

  useEffect(() => {
    if (initialInvoice) {
      setInvoice(initialInvoice);
    } else if (invoiceId) {
      setInvoice(null);
    }
  }, [initialInvoice, invoiceId]);

  const handleApprove = () => {
    const amount = approvalData.approveFullAmount 
      ? totalAmount
      : parseFloat(approvalData.partialAmount);

    if (!approvalData.approveFullAmount && (!amount || amount <= 0 || amount > totalAmount)) {
      alert('Please enter a valid partial amount');
      return;
    }

    const processingFee = amount * 0.01; // 1% processing fee
    const netPayout = amount - processingFee;

    onApprove({
      invoiceId,
      amount,
      remarks: approvalData.remarks,
      interestRate: approvalData.interestRate,
      duration: approvalData.duration,
      processingFee,
      netPayout
    });
  };

  const handleReject = () => {
    if (!rejectionData.reason) {
      alert('Please select a rejection reason');
      return;
    }
    if (!rejectionData.comments || rejectionData.comments.length < 20) {
      alert('Please provide detailed comments (minimum 20 characters)');
      return;
    }

    onReject({
      invoiceId,
      reason: rejectionData.reason,
      comments: rejectionData.comments
    });
  };

  const calculateFinancing = () => {
    const amount = approvalData.approveFullAmount 
      ? totalAmount 
      : parseFloat(approvalData.partialAmount) || 0;
    
    const processingFee = amount * 0.01;
    const netPayout = amount - processingFee;

    return {
      amount,
      processingFee: processingFee.toFixed(2),
      netPayout: netPayout.toFixed(2)
    };
  };

  const documentUrl = invoice?.documentPath || invoice?.documentUrl || '';
  const hasDocument = Boolean(documentUrl);
  
  // Debug: Log the document URL
  console.log('Invoice Document URL:', documentUrl);
  console.log('Has Document:', hasDocument);

  const handleDownload = async (e) => {
    e.preventDefault();
    if (!hasDocument || !documentUrl) return;

    try {
      // Fetch the PDF file
      const response = await fetch(documentUrl);
      const blob = await response.blob();
      
      // Create a temporary URL for the blob
      const blobUrl = window.URL.createObjectURL(blob);
      
      // Create a temporary anchor element and trigger download
      const link = document.createElement('a');
      link.href = blobUrl;
      link.download = `invoice-${invoice.invoiceNumber || invoice.invoiceId}.pdf`;
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      document.body.removeChild(link);
      window.URL.revokeObjectURL(blobUrl);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      // Fallback to direct download link
      window.open(documentUrl, '_blank');
    }
  };

  const handleZoomIn = () => {
    setPdfZoom(prev => Math.min(prev + 25, 200));
  };

  const handleZoomOut = () => {
    setPdfZoom(prev => Math.max(prev - 25, 50));
  };

  const handleRotateLeft = () => {
    setPdfRotation(prev => (prev - 90) % 360);
  };

  const handleRotateRight = () => {
    setPdfRotation(prev => (prev + 90) % 360);
  };

  const handleOpenInNewTab = () => {
    if (documentUrl) {
      window.open(documentUrl, '_blank');
    }
  };

  const handleMouseWheel = (e) => {
    // Check if Ctrl key is pressed (for zoom)
    if (e.ctrlKey) {
      e.preventDefault();
      if (e.deltaY < 0) {
        // Scroll up = Zoom in
        setPdfZoom(prev => Math.min(prev + 10, 200));
      } else {
        // Scroll down = Zoom out
        setPdfZoom(prev => Math.max(prev - 10, 50));
      }
    }
  };

  const renderLabelValue = (
    label,
    value,
    { boldValue = false, blockValue = false, justify = 'flex-start' } = {}
  ) => (
    <div style={{ display: 'flex', marginBottom: '6px', justifyContent: justify }}>
      <span style={{ fontWeight: 600, color: '#555', minWidth: '100px' }}>{label}</span>
      <span
        style={{
          color: '#333',
          fontWeight: boldValue ? 600 : 400,
          display: blockValue ? 'block' : 'inline',
          whiteSpace: blockValue ? 'pre-line' : 'normal'
        }}
      >
        {value || 'N/A'}
      </span>
    </div>
  );
  const baseAmount = invoice?.baseAmount ?? invoice?.amounts?.base ?? 0;
  const cgstAmount = invoice?.cgstAmount ?? invoice?.amounts?.cgst ?? 0;
  const sgstAmount = invoice?.sgstAmount ?? invoice?.amounts?.sgst ?? 0;
  const igstAmount = invoice?.igstAmount ?? invoice?.amounts?.igst ?? 0;
  const totalAmount = invoice?.totalAmount ?? invoice?.amounts?.total ?? 0;
  const supplierName = invoice?.supplier?.name || invoice?.supplierName || 'N/A';
  const supplierGST = invoice?.supplier?.gst || invoice?.supplierGST || 'N/A';
  const supplierAddress = invoice?.supplier?.address || invoice?.supplierAddress || 'N/A';
  const supplierContact = invoice?.supplier?.contact || invoice?.contactPersonName || 'N/A';
  const supplierPhone = invoice?.supplier?.phone || invoice?.contactMobile || 'N/A';
  const supplierEmail = invoice?.supplier?.email || invoice?.contactEmail || 'N/A';

  if (loading || !invoice) {
    return (
      <div className="modal-backdrop show">
        <div className="modal show" style={{ display: 'block' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-body text-center" style={{ padding: '50px' }}>
                <i className="fa fa-spinner fa-spin fa-3x"></i>
                <p style={{ marginTop: '15px' }}>Loading invoice details...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-backdrop show" style={{ overflow: 'hidden' }}>
      <div className="modal show" style={{ display: 'block', overflow: 'hidden', padding: '20px 0' }}>
        <div className="modal-dialog" style={{ maxWidth: '95%', width: '1400px', height: 'calc(100vh - 40px)', margin: '0 auto', display: 'flex', flexDirection: 'column' }}>
          <div className="modal-content" style={{ borderRadius: '0', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            {/* Modal Header */}
            <div className="modal-header" style={{ padding: '17px 30px', background: '#f7f9fa', flexShrink: 0 }}>
              <h4 className="modal-title">
                <i className="fa fa-file-text-o"></i> Invoice Details
              </h4>
              <button 
                type="button" 
                className="close" 
                onClick={onClose}
                style={{ fontSize: '28px', marginTop: '-33px' }}
              >
                &times;
              </button>
            </div>

            {/* Modal Body - Landscape Layout */}
            <div className="modal-body" style={{ flex: 1, overflow: 'hidden', padding: '20px', display: 'flex' }}>
              {/* Left Column - Invoice Document Preview (Static) */}
              <div style={{ width: '50%', paddingRight: '15px', borderRight: '1px solid #ddd', display: 'flex', flexDirection: 'column' }}>
                {/* Invoice Document */}
                <div className="invoice-document-section" style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px', flexShrink: 0 }}>
                    <h5 style={{ marginBottom: 0, fontWeight: '600' }}>
                      <i className="fa fa-file-pdf-o"></i> Invoice Document
                    </h5>
                  </div>
                  
                  {/* PDF Controls */}
                  {hasDocument && (
                    <div style={{ 
                      display: 'flex', 
                      gap: '8px', 
                      marginBottom: '10px', 
                      padding: '8px', 
                      backgroundColor: '#f5f5f5', 
                      borderRadius: '4px',
                      flexShrink: 0,
                      alignItems: 'center',
                      flexWrap: 'wrap'
                    }}>
                      <button 
                        onClick={handleZoomOut}
                        className="btn btn-default btn-sm"
                        title="Zoom Out"
                        disabled={pdfZoom <= 50}
                      >
                        <i className="fa fa-search-minus"></i>
                      </button>
                      <span style={{ padding: '0 8px', fontSize: '13px', fontWeight: '500' }}>{pdfZoom}%</span>
                      <button 
                        onClick={handleZoomIn}
                        className="btn btn-default btn-sm"
                        title="Zoom In"
                        disabled={pdfZoom >= 200}
                      >
                        <i className="fa fa-search-plus"></i>
                      </button>
                      
                      <div style={{ borderLeft: '1px solid #ddd', height: '24px', margin: '0 4px' }}></div>
                      
                      <button 
                        onClick={handleRotateLeft}
                        className="btn btn-default btn-sm"
                        title="Rotate Left"
                      >
                        <i className="fa fa-rotate-left"></i>
                      </button>
                      <button 
                        onClick={handleRotateRight}
                        className="btn btn-default btn-sm"
                        title="Rotate Right"
                      >
                        <i className="fa fa-rotate-right"></i>
                      </button>
                      
                      <div style={{ borderLeft: '1px solid #ddd', height: '24px', margin: '0 4px' }}></div>
                      
                      <button 
                        onClick={handleOpenInNewTab}
                        className="btn btn-default btn-sm"
                        title="Open in New Tab"
                      >
                        <i className="fa fa-external-link"></i>
                      </button>
                      <button 
                        onClick={handleDownload}
                        className="btn btn-primary btn-sm"
                        title="Download"
                      >
                        <i className="fa fa-download"></i>
                      </button>
                    </div>
                  )}
                  
                  <div 
                    className="document-preview" 
                    style={{ 
                      border: '1px solid #ddd', 
                      padding: '8px', 
                      backgroundColor: '#f9f9f9', 
                      flex: 1, 
                      overflow: 'auto', 
                      display: 'flex', 
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'flex-start'
                    }}
                    onWheel={handleMouseWheel}
                  >
                    {hasDocument ? (
                      <div style={{ 
                        width: '100%', 
                        height: '100%',
                        transform: `scale(${pdfZoom / 100}) rotate(${pdfRotation}deg)`,
                        transformOrigin: 'top center',
                        transition: 'transform 0.3s ease'
                      }}>
                        <iframe
                          src={`${documentUrl}#toolbar=0&navpanes=0&scrollbar=1&view=FitH`}
                          title="Invoice Preview"
                          style={{ 
                            width: '100%', 
                            height: '100%', 
                            minHeight: '600px', 
                            border: 'none', 
                            backgroundColor: '#fff' 
                          }}
                          allow="fullscreen"
                        />
                      </div>
                    ) : (
                      <div style={{ textAlign: 'center', padding: '60px 20px' }}>
                        <i className="fa fa-file-pdf-o fa-3x" style={{ color: '#ccc' }}></i>
                        <p style={{ marginTop: '10px', color: '#999' }}>Invoice document not available</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Right Column - Invoice Details (Scrollable) */}
              <div style={{ width: '50%', paddingLeft: '15px', overflowY: 'auto', maxHeight: '100%' }}>
              {/* Invoice Information */}
              <div className="invoice-info-section" style={{ marginBottom: '25px', borderBottom: '1px solid #eee', paddingBottom: '20px' }}>
                <div className="row">
                  <div className="col-md-7">
                    {renderLabelValue('Invoice No:', invoice.invoiceNumber, { boldValue: true })}
                    {renderLabelValue('Invoice Date:', formatDate(invoice.invoiceDate))}
                  </div>
                  <div className="col-md-5" style={{ textAlign: 'right' }}>
                    <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: '10px', marginBottom: '6px' }}>
                      <span style={{ fontWeight: 600, color: '#555' }}>Status:</span>
                      {getStatusBadge(invoice.status)}
                    </div>
                    <div style={{ display: 'inline-flex', justifyContent: 'flex-end', gap: '10px', fontWeight: 600, color: '#555' }}>
                      <span>Due Date:</span>
                      <span style={{ fontWeight: 500, color: '#333' }}>{formatDate(invoice.dueDate)}</span>
                    </div>
                  </div>
                </div>
              </div>

              <hr />

              {/* Supplier Information */}
              <div className="supplier-info-section">
                <h5 style={{ marginBottom: '19px', fontWeight: '600' }}>
                  <i className="fa fa-building"></i> Supplier Information
                </h5>
                <div className="row">
                  <div className="col-md-6">
                    {renderLabelValue('Name:', supplierName, { boldValue: true })}
                    {renderLabelValue('GST:', supplierGST)}
                    {renderLabelValue('Address:', supplierAddress, { blockValue: true })}
                  </div>
                  <div className="col-md-6">
                    {renderLabelValue('Contact:', supplierContact, { boldValue: true })}
                    {renderLabelValue('Phone:', supplierPhone)}
                    {renderLabelValue('Email:', supplierEmail)}
                  </div>
                </div>
              </div>

              <hr />

              {/* Amount Breakdown */}
              <div className="amount-breakdown-section">
                <h5 style={{ marginBottom: '19px', fontWeight: '600' }}>
                  <i className="fa fa-calculator"></i> Amount Breakdown
                </h5>
                <table className="table table-bordered">
                  <tbody>
                    <tr>
                      <td>Base Amount:</td>
                      <td className="text-right">₹ {formatAmount(baseAmount)}</td>
                    </tr>
                    <tr>
                      <td>CGST (9%):</td>
                      <td className="text-right">₹ {formatAmount(cgstAmount)}</td>
                    </tr>
                    <tr>
                      <td>SGST (9%):</td>
                      <td className="text-right">₹ {formatAmount(sgstAmount)}</td>
                    </tr>
                    <tr>
                      <td>IGST (18%):</td>
                      <td className="text-right">₹ {formatAmount(igstAmount)}</td>
                    </tr>
                    <tr style={{ fontWeight: 'bold', fontSize: '16px' }}>
                      <td>Total Amount:</td>
                      <td className="text-right" style={{ color: '#1976D2' }}>
                        ₹ {formatAmount(totalAmount)}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>


              {/* Approval Section - Only show for Pending invoices */}
              {invoice.status === 'Pending' && !showRejectForm && (
                <div className="approval-section">
                <h5 style={{ marginBottom: '19px', fontWeight: '600', color: '#4CAF50' }}>
                    <i className="fa fa-check-circle"></i> Approval Actions
                  </h5>
                  
                  <div className="form-group">
                    <label>
                      <input
                        type="radio"
                        checked={approvalData.approveFullAmount}
                        onChange={() => setApprovalData({ ...approvalData, approveFullAmount: true })}
                        style={{ marginRight: '8px' }}
                      />
                      Approve Full Amount: <strong>₹ {formatAmount(invoice.amounts?.total)}</strong>
                    </label>
                  </div>

                  <div className="form-group">
                    <label>
                      <input
                        type="radio"
                        checked={!approvalData.approveFullAmount}
                        onChange={() => setApprovalData({ ...approvalData, approveFullAmount: false })}
                        style={{ marginRight: '8px' }}
                      />
                      Approve Partial Amount:
                    </label>
                    {!approvalData.approveFullAmount && (
                      <input
                        type="number"
                        className="form-control"
                        placeholder="Enter amount"
                        value={approvalData.partialAmount}
                        onChange={(e) => setApprovalData({ ...approvalData, partialAmount: e.target.value })}
                        style={{ marginTop: '10px' }}
                      />
                    )}
                  </div>

                  <div className="form-group">
                    <label>Remarks (Optional):</label>
                    <textarea
                      className="form-control"
                      rows="3"
                      placeholder="Add any comments or notes..."
                      maxLength="500"
                      value={approvalData.remarks}
                      onChange={(e) => setApprovalData({ ...approvalData, remarks: e.target.value })}
                    ></textarea>
                    <small className="text-muted">
                      {approvalData.remarks.length}/500 characters
                    </small>
                  </div>

                  <div className="financing-estimate" style={{ backgroundColor: '#E3F2FD', padding: '15px', borderRadius: '8px', marginBottom: '15px' }}>
                    <h6 style={{ marginBottom: '10px', fontWeight: '600' }}>Expected Financing:</h6>
                    <div className="row">
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>Rate of Interest (%):</label>
                          <input
                            type="number"
                            className="form-control"
                            step="0.1"
                            value={approvalData.interestRate}
                            onChange={(e) => setApprovalData({ ...approvalData, interestRate: e.target.value })}
                          />
                        </div>
                      </div>
                      <div className="col-md-6">
                        <div className="form-group">
                          <label>Duration (days):</label>
                          <input
                            type="number"
                            className="form-control"
                            value={approvalData.duration}
                            onChange={(e) => setApprovalData({ ...approvalData, duration: e.target.value })}
                          />
                        </div>
                      </div>
                    </div>
                    <div className="financing-summary">
                      <div><strong>Processing Fee:</strong> ₹ {calculateFinancing().processingFee}</div>
                      <div style={{ fontSize: '16px', color: '#1976D2', marginTop: '5px' }}>
                        <strong>Net Payout:</strong> ₹ {calculateFinancing().netPayout}
                      </div>
                    </div>
                  </div>

                  <div className="approval-actions">
                    <button 
                      className="btn btn-success btn-lg"
                      onClick={handleApprove}
                    >
                      <i className="fa fa-check"></i> Approve Invoice
                    </button>
                    <button 
                      className="btn btn-danger btn-lg"
                      onClick={() => setShowRejectForm(true)}
                      style={{ marginLeft: '10px' }}
                    >
                      <i className="fa fa-times"></i> Reject Invoice
                    </button>
                  </div>
                </div>
              )}

              {/* Rejection Form */}
              {showRejectForm && (
                <div className="rejection-section">
                <h5 style={{ marginBottom: '19px', fontWeight: '600', color: '#F44336' }}>
                    <i className="fa fa-times-circle"></i> Rejection Reason
                  </h5>

                  <div className="form-group">
                    <label>Select Reason: <span className="text-danger">*</span></label>
                    <select
                      className="form-control"
                      value={rejectionData.reason}
                      onChange={(e) => setRejectionData({ ...rejectionData, reason: e.target.value })}
                    >
                      <option value="">-- Select Reason --</option>
                      <option value="details_mismatch">Invoice details mismatch</option>
                      <option value="goods_not_received">Goods not received</option>
                      <option value="quality_issues">Quality issues</option>
                      <option value="pricing_discrepancy">Pricing discrepancy</option>
                      <option value="duplicate">Duplicate invoice</option>
                      <option value="other">Other (please specify)</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Additional Comments: <span className="text-danger">*</span></label>
                    <textarea
                      className="form-control"
                      rows="4"
                      placeholder="Please provide detailed reason for rejection (minimum 20 characters)..."
                      value={rejectionData.comments}
                      onChange={(e) => setRejectionData({ ...rejectionData, comments: e.target.value })}
                    ></textarea>
                    <small className="text-muted">
                      {rejectionData.comments.length} characters (minimum 20 required)
                    </small>
                  </div>

                  <div className="rejection-actions">
                    <button 
                      className="btn btn-danger"
                      onClick={handleReject}
                    >
                      <i className="fa fa-times"></i> Confirm Rejection
                    </button>
                    <button 
                      className="btn btn-default"
                      onClick={() => setShowRejectForm(false)}
                      style={{ marginLeft: '10px' }}
                    >
                      Cancel
                    </button>
                  </div>
                </div>
              )}

              <hr />

              {/* Timeline */}
              <div className="timeline-section">
                <h5 style={{ marginBottom: '19px', fontWeight: '600' }}>
                  <i className="fa fa-history"></i> Invoice Timeline
                </h5>
                <div className="timeline">
                  {(invoice.timeline || []).map((event, index) => (
                    <div key={index} className="timeline-item">
                      <div className="timeline-marker"></div>
                      <div className="timeline-content">
                        <div className="timeline-event">{event.event}</div>
                        <div className="timeline-date">{formatTimelineDate(event.timestamp || event.date)}</div>
                        {event.description && <div className="timeline-detail">{event.description}</div>}
                        {event.by && <div className="timeline-detail">By: {event.by}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// Helper Functions
const formatAmount = (amount) => {
  const numericValue = typeof amount === 'string' ? parseFloat(amount) : amount;
  if (!Number.isFinite(numericValue)) {
    return '0.00';
  }
  return numericValue.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

const formatTimelineDate = (timestamp) => {
  if (!timestamp) return '-';
  const date = new Date(timestamp);
  return date.toLocaleString('en-IN', { day: '2-digit', month: 'short', year: 'numeric', hour: 'numeric', minute: '2-digit' });
};

const formatDate = (dateString) => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-IN', { 
    day: '2-digit', 
    month: 'short', 
    year: 'numeric' 
  });
};

const getStatusBadge = (status) => {
  const statusConfig = {
    'Pending': { icon: '🟡', color: '#FF9800', bg: '#FFF3E0', label: 'Pending' },
    'Approved': { icon: '✅', color: '#4CAF50', bg: '#E8F5E9', label: 'Approved' },
    'Rejected': { icon: '❌', color: '#F44336', bg: '#FFEBEE', label: 'Rejected' }
  };

  const config = statusConfig[status] || statusConfig['Pending'];

  return (
    <span 
      style={{
        backgroundColor: config.bg,
        color: config.color,
        padding: '5px 12px',
        borderRadius: '12px',
        fontSize: '13px',
        fontWeight: '600',
        display: 'inline-block'
      }}
    >
      {config.icon} {config.label}
    </span>
  );
};

export default InvoiceDetailModal;

